import { Subscription } from 'rxjs';
import { CategoriesService } from 'src/app/core/services/categories.service';
import { NotificationService } from 'src/app/core/services/notification.service';
import * as XLSX from 'xlsx';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { PageParams } from '../../../core/models/page-params.model';
import { UploadFileService } from '../../../core/services/upload-file.service';
import { UserService } from '../../../core/services/user.service';
import { ProductStatus } from '../../../shared/enums/product-status.enum';
import { ConfirmPopupDialog } from '../../../shared/popups/confirm-popup/confirm-popup.component';
import { ExcelInfoPopupDialog } from '../../../shared/popups/excel-info-popup/excel-info-popup.component';
import { ShopCategoriesService } from '../../customer-management/services/shop-categories.service';
import { IEmpactProductResponse } from '../models/empact-product-details.interface';
import { OfflineShopService } from '../services/offline-shop.service';
import { IProductFilter } from './models/product-filter.interface';
import { OfflineProductComponent } from './product-details/offline-product.component';
import { ProductImgUpload } from 'src/app/shared/popups/product-img-upload/product-img-upload.component';

@Component({
    selector: 'eci-products',
    templateUrl: './products.component.html',
    styleUrls: ['./products.component.scss']
})
export class ProductsComponent implements OnInit {

    public pageParams: PageParams = new PageParams();
    public downloadStatus: number = 0;
    public productsList: IEmpactProductResponse[];
    private currentSort: Sort = {active: 'createdAt', direction: 'desc'};
    public productFilter: IProductFilter = {};
    private shopsId: string[] = [];
    public isShopNotFound = false;
    public isFirstProductAdded = false;
    public companyId: number | string = 'all';
    public totalWithoutHSCode: number = 0;
    public displayedColumns: string[] = [
        'name',
        'shopName',
        'shopCategoryName',
        'salePrice',
        'costPriceUnit',
        'weight',
        'volume',
        'availableQuantity',
        'brand',
        'eanUpc',
        'sku',
        'secondarySku',
        'leadTime',
        'hsCode',
        'shopId',
        'status',
    ];

    @ViewChild('fileInput2') fileInput2;

    public dataSource: MatTableDataSource<IEmpactProductResponse> = new MatTableDataSource<IEmpactProductResponse>();
    public subscription: Subscription;
    public showLoader = false;

    constructor(
        private shopSvc: OfflineShopService,
        public userSvc: UserService,
        private formBuilder: FormBuilder,
        private dialog: MatDialog,
        private notify: NotificationService,
        private categoriesSvc: CategoriesService,
        public uploadFileSvc: UploadFileService,
        private categoriesService: ShopCategoriesService,
        private productsSvc: OfflineShopService,
    ) {
    }

    ngOnInit(): void {
        this.initializeFilters();
        this.getEShops();
        this.getDownloadTemplateStatus();

        this.subscription = this.productsSvc.amountProductsWithoutHSCode$.subscribe((value) => {
            this.totalWithoutHSCode = value;
        });
        this.productsSvc.updateShopProductList.subscribe((value) => {
            if (value !== null) {
                this.getList();
            }
        });
    }

    ngOnDestroy(): void {
        this.shopSvc.filters$.next({
            page: this.pageParams.pageIndex,
            limit: this.pageParams.pageSize,
            orderField: this.currentSort?.active || 'createdAt',
            orderDir: this.currentSort?.direction?.toUpperCase() || 'DESC',
            productFilter: this.productFilter,
        });
    }

    private initializeFilters(filters = this.shopSvc.filters$.getValue()): void {
        this.pageParams.pageSize = filters?.pageSize || 100;
        this.pageParams.pageIndex = filters?.pageIndex || 0;
        this.currentSort.direction = filters?.orderDir || 'desc';
        this.currentSort.active = filters?.orderField || 'createdAt';
        this.companyId = this.userSvc.isOfflineCustomer ? this.userSvc.userStorage?.company?.id : 'all';
        this.productFilter = filters?.productFilter || {companyId: this.companyId};
    }

    public pageChanged(e: PageParams): void {
        this.pageParams.pageIndex = e.pageIndex;
        this.pageParams.pageSize = e.pageSize;
        this.getList(e.pageIndex + 1, e.pageSize);
    }

    public sortData(sort: Sort): void {
        const data = this.productsList.slice();
        if (!sort.active || sort.direction === '') {
            this.productsList = data;
            this.dataSource = new MatTableDataSource<IEmpactProductResponse>(this.productsList);
            return;
        }

        this.currentSort = sort;
        this.getList(this.pageParams?.pageIndex + 1, this.pageParams?.pageSize);
    }

    private getList(page: number = this.pageParams?.pageIndex + 1, limit: number = this.pageParams?.pageSize): void {
        if (!this.shopsId || this.shopsId.length === 0) {
            this.productsList = [];
            this.dataSource = new MatTableDataSource<IEmpactProductResponse>(this.productsList);
            return;
        }
        this.shopSvc.getProducts(page, limit, this.productFilter,
            this.currentSort.active, this.currentSort.direction?.toUpperCase(), this.shopsId)
            .subscribe((data) => {
                if (!data) {
                    return;
                }
                this.productsList = data.items;
                this.dataSource = data.items;
                this.pageParams.pageSize = data.meta.itemsPerPage;
                this.pageParams.pageIndex = data.meta.currentPage - 1;
                this.pageParams.length = data.meta.totalItems;
                if (this.isFirstProductAdded) {
                    this.confirmDialog(`Go to B2B Client from the left navigation bar and click on'+' button to add your B2B Clients`);
                }
            });
    }

    openDialog(): void {
        if (isNaN(+this.companyId)) {
            this.notify.showWarn('Please choose customer first');
            return;
        }
        const dialogRef = this.dialog.open(OfflineProductComponent,
            {maxHeight: '600px'},
        );
        dialogRef.componentInstance.createMode = true;
        dialogRef.componentInstance.marginTop = '0';
        dialogRef.componentInstance.companyCode = +this.companyId;
        dialogRef.componentInstance.shopsId = this.shopsId;
        dialogRef.afterClosed()
            .subscribe((result) => {
                this.isFirstProductAdded = this.productsList?.length === 0 && !this.isAdmin;
                this.getList(1, 100);
            });
    }

    changeStatus(event: { id: number, status: ProductStatus }): void {
        this.shopSvc.changeStatus(event.id, event.status)
            .subscribe(() => {
                this.notify.showSuccessMessage('Status changed successfully');
            });
    }

    public exportToExcel(): void {
        this.shopSvc.getProductsExcel(this.pageParams?.pageIndex + 1, this.pageParams?.pageSize, this.productFilter,
            this.currentSort.active, this.currentSort.direction?.toUpperCase(), this.shopsId)
            .subscribe(() => {
            });
    }

    get isAdmin(): boolean {
        return this.userSvc.isAdmin || this.userSvc.isDelegatedAdmin;
    }

    public getEShops(): void {
        this.shopSvc.getOfflineShop(this.companyId)
            .subscribe((shops) => {
                if (!shops || shops.length === 0) {
                    this.isShopNotFound = true;
                    this.shopsId = [];
                    this.notify.showWarn('Due to the absence of the Empact shop,  product is not added. Please add your Empact shop in the Customer profile.');
                    return;
                }
                this.isShopNotFound = false;
                this.shopsId = shops.map((shop) => shop.shopId);
            }, () => {
            }, () => {
                this.getList();
                this.getDownloadTemplateStatus();
            });
    }

    private getDownloadTemplateStatus(): void {
        if (!this.shopsId || this.shopsId.length === 0 || this.isAdmin) {
            this.downloadStatus = 1;
            return;
        }
        this.productsSvc.getDownloadStatus(this.shopsId[0]).subscribe((data) => {
            this.downloadStatus = data;
        }, (error) => {
            // this.notify.showError(error.message);
        });
    }

    public uploadProducts(event): void {
        if (isNaN(+this.companyId)) {
            this.notify.showWarn('Please choose customer first');
            return;
        }
        this.showLoader = true;
        const files = event.target.files;
        if (!files || files.length === 0) {
            return;
        }
        const file = files[0];
        const fileReader = new FileReader();
        fileReader.readAsBinaryString(file);
        fileReader.onload = function(f) {
            const workbook = XLSX.read(<string> fileReader.result, {type: 'binary', cellDates: true, cellStyles: true});

            const sheet_name_list = workbook.SheetNames;
            const products: any[] = XLSX.utils.sheet_to_json(workbook.Sheets[sheet_name_list[0]]);
            const productsToCreate = [];
            products.forEach((p) => {
                const product = this.getProduct(p);
                productsToCreate.push(product);
            });
            if (productsToCreate.length === 0) {
                this.showLoader = false;
                this.notify.showWarn('Cant find valid data in file');
                return;
            }
            this.productsSvc.createProductFromXLSX(productsToCreate)
                .subscribe((data) => {
                    this.showLoader = false;
                    if (!data) {
                        return;
                    }
                    if (data?.failed > 0 && data?.success > 0) {
                        this.notify.showWarn(`Successfully upload: ${data?.success} \r\n Failed: ${data?.failed}`);
                    } else if (data?.failed > 0 && data?.success == 0) {
                        this.notify.showError(`Successfully upload: ${data?.success} \r\n Failed: ${data?.failed}`);
                    } else {
                        this.notify.showSuccessMessage(`Successfully upload: ${data?.success} \r\n Failed: ${data?.failed}`);
                    }
                    data.header = 'Ecommerce Product';
                    data.empactStatus.header = 'Empact Product';
                    this.infoDialog([data]);
                    this.getList(1, 100);
                    if (data.success > 0) {
                        this.confirmDialog(`After loading the products are missing details. Need to fill additional information of the product (images, details, etc.)`);
                    }
                }, (err) => {
                    this.notify.showValidationError(err);
                    this.showLoader = false;
                });
        }.bind(this);
        event.target.value = '';
        fileReader.onerror = () => {
            event.target.value = '';
        };
    }

    public confirmDialog(message: string): void {
        const dialogRef = this.dialog.open(ConfirmPopupDialog);
        dialogRef.componentInstance.message = message;
        dialogRef.componentInstance.leftBtnText = 'Ok';
        dialogRef.componentInstance.header = 'Notification';
        dialogRef.componentInstance.showRightBtn = false;
    }

    private infoDialog(data, header = 'Excel Sheet Information'): void {
        const dialogRef = this.dialog.open(ExcelInfoPopupDialog, {
            height: '400px',
            width: '600px',
        });
        dialogRef.componentInstance.listOfInfo = data;
        dialogRef.componentInstance.rightBtnText = 'Ok';
        dialogRef.componentInstance.header = header;
        dialogRef.afterClosed()
            .subscribe((value) => {
            });
    }

    private getProduct(product): any {
        return {
            name: product['Product Name*'],
            shopId: product.shopId || this.shopsId[0],
            shopCategoryName: product['Product Category Name'],
            sku: product['Sku*'],
            secondarySku: product['Sku Description'],
            costPriceUnit: product['Cost Price Unit*'],
            salePrice: product['Sale Price*'],
            eanUpc: product['EAN UPC'] || '',
            leadTime: product['Lead Time'] || null,
            availableQuantity: product['Available Quantity'] || 0,
            volume: parseFloat(product['Volume']) || 0,
            brand: product['Brand'] || null,
            size: product['Size'] || null,
            color: product['Color'] || null,
            colorCode: product['Color Code'] || null,
            weight: parseFloat(product['Weight']) || 0,
            status: (['Active', 1 , 'Available'].includes(product['Available / Not Available'])) ? ProductStatus.Active :
                (product['Available / Not Available'] === undefined ? ProductStatus.Active : ProductStatus.NotActive),
            companyId: this.companyId,
        };
    }

    public downloadExcel(): void {
        this.productsSvc.updateDownloadStatus(this.shopsId).subscribe((data) => {
            this.downloadStatus = 1;
        }, (error) => {
        });
        this.productsSvc.downloadEcommarceExcelExample()
            .subscribe((data) => {
                this.uploadFileSvc.saveToFileSystem(data, 'ecommerce_product_example.xlsx');
            });
    }

    public reloadData(_data: any): void {
        this.getList();
    }

    public onCompanyUpdate(id): void {
        this.companyId = id;
        this.getEShops();
    }

    public onFilter(filters: IProductFilter): void {
        this.productFilter = filters;
        filters.companyId = this.companyId;
        this.getList(1, this.pageParams?.pageSize);
    }

    downloadXlsxImgTemplate(): void {
        this.productsSvc.downloadProductImageExcelTemplate()
            .subscribe((data) => {
                this.uploadFileSvc.saveToFileSystem(data, 'product_images_example.xlsx');
        });
    }

    public uploadProductImages(event): void {
        if (isNaN(+this.companyId)) {
            this.notify.showWarn('Please choose customer first');
            this.fileInput2.nativeElement.value = '';
            return;
        }
        this.showLoader = true;
        const files = event.target.files;
        if (!files || files.length === 0) {
            this.fileInput2.nativeElement.value = '';
            return;
        }
        const file: File = files[0];

        let imagesData: FormData = new FormData();
        imagesData.append('content', file, file?.name);

        this.productsSvc.uploadImages4Products(imagesData, +this.companyId).subscribe(data => {
            this.uploadImgDialog(data);
        }, () => {
        }, () => {
            this.fileInput2.nativeElement.value = '';
            this.showLoader = false;
        });
    }

    private uploadImgDialog(data, header = 'Images upload results'): void {
        const dialogRef = this.dialog.open(ProductImgUpload, {
            height: '400px',
            width: '600px',
        });
        dialogRef.componentInstance.data = data;
        dialogRef.componentInstance.rightBtnText = 'Ok';
        dialogRef.componentInstance.header = header;
    }
}
